import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import { EditableRow, SettingsCard, SettingsRow, SettingsSection } from './settings-ui';
import { useOrganization, useSettingsUsers, useUpdateOrganization } from './useSettings';
import type { SettingsUser } from './settings-api';

/**
 * Settings › Organization (SCRUM-460): bank profile card — editable bank name,
 * the primary-admin user picker (assigns from the real roster), and support
 * phone. Ports `renderSettingsSectionOrg` (app-shell 7.html) to token styles.
 */

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join('')
    .toUpperCase();
}

export function OrganizationSection() {
  const { organization, isLoading, isError } = useOrganization();
  const { users } = useSettingsUsers();
  const { save } = useUpdateOrganization();

  if (isError) {
    return (
      <SettingsSection>
        <SettingsCard title="Bank profile">
          <p className="text-text-3 py-2 text-[0.8125rem]">
            Organization settings are unavailable right now.
          </p>
        </SettingsCard>
      </SettingsSection>
    );
  }

  if (isLoading || !organization) {
    return (
      <SettingsSection>
        <SettingsCard title="Bank profile">
          <p className="text-text-3 py-2 text-[0.8125rem]">Loading…</p>
        </SettingsCard>
      </SettingsSection>
    );
  }

  return (
    <SettingsSection>
      <SettingsCard title="Bank profile">
        <EditableRow
          label="Bank name"
          value={organization.bankName}
          onSave={(bankName) => save({ bankName })}
        />

        <PrimaryAdminRow
          currentName={organization.primaryAdminName}
          currentEmail={organization.primaryAdminEmail}
          selectedUserId={organization.primaryAdminUserId}
          users={users}
          onPick={(userId) => {
            void save({ primaryAdminUserId: userId });
          }}
        />

        <EditableRow
          label="Support phone"
          value={organization.supportPhone ?? ''}
          onSave={(supportPhone) => save({ supportPhone })}
        />
      </SettingsCard>
    </SettingsSection>
  );
}

function PrimaryAdminRow({
  currentName,
  currentEmail,
  selectedUserId,
  users,
  onPick,
}: {
  currentName: string;
  currentEmail: string;
  selectedUserId: string | null;
  users: SettingsUser[];
  onPick: (userId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const selected = users.find((u) => u.id === selectedUserId);
  const displayName = selected?.displayName ?? currentName;
  const displayEmail = selected?.email ?? currentEmail;

  return (
    <SettingsRow label="Primary admin" sub="Assigned from your roster">
      <div ref={rootRef} className="relative">
        <button
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => {
            setOpen((v) => !v);
          }}
          className="bg-bg-3 border-border-1 hover:border-border-2 rounded-tk-md text-text-0 font-body inline-flex items-center gap-2.5 border px-2.5 py-1.5"
        >
          <span className="bg-brand-dim text-brand-primary rounded-tk-full inline-flex h-7 w-7 flex-shrink-0 items-center justify-center font-mono text-[0.625rem] font-semibold">
            {initials(displayName)}
          </span>
          <span className="flex flex-col gap-px text-left">
            <span className="text-text-0 text-[0.8125rem] font-medium">{displayName}</span>
            <span className="text-text-3 text-[0.6875rem]">{displayEmail}</span>
          </span>
          <svg
            viewBox="0 0 10 10"
            width="9"
            height="9"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className="text-text-3"
          >
            <polyline points="2.5 4 5 6.5 7.5 4" />
          </svg>
        </button>

        {open && (
          <div
            role="menu"
            aria-label="Assign primary admin"
            className="bg-bg-4 border-border-1 rounded-tk-md shadow-tk-md absolute top-[calc(100%+6px)] right-0 z-[100] flex max-h-[360px] w-[320px] max-w-[min(320px,calc(100vw-2rem))] flex-col overflow-hidden border"
          >
            <div className="text-text-3 border-border-0 border-b px-3 pt-2.5 pb-2 font-mono text-[0.625rem] font-semibold tracking-[0.08em] uppercase">
              Assign primary admin
            </div>
            <div className="overflow-y-auto p-1">
              {users.map((u) => {
                const picked = u.id === selectedUserId;
                return (
                  <button
                    key={u.id}
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      onPick(u.id);
                      setOpen(false);
                    }}
                    className={cn(
                      'rounded-tk-sm hover:bg-bg-3 flex w-full items-center gap-2.5 border-0 bg-transparent px-2.5 py-2 text-left',
                      picked && 'bg-brand-dim',
                    )}
                  >
                    <span className="bg-brand-dim text-brand-primary rounded-tk-full inline-flex h-7 w-7 flex-shrink-0 items-center justify-center font-mono text-[0.625rem] font-semibold">
                      {initials(u.displayName)}
                    </span>
                    <span className="flex min-w-0 flex-col gap-px text-left">
                      <span
                        className={cn(
                          'truncate text-[0.8125rem] font-medium',
                          picked ? 'text-brand-primary' : 'text-text-0',
                        )}
                      >
                        {u.displayName}
                      </span>
                      <span className="text-text-3 truncate text-[0.6875rem]">
                        {u.role} · {u.email}
                      </span>
                    </span>
                    {picked && (
                      <svg
                        viewBox="0 0 12 12"
                        width="11"
                        height="11"
                        fill="none"
                        stroke="var(--cyan)"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="3 6.5 5 8.5 9 4" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </SettingsRow>
  );
}
